/**
 * Session helpers for the Boost-Your-Policy generation page.
 *
 * The generation page caches the generated/regenerated policy in
 * `sessionStorage` under SESSION_RESULT_KEY so the branded editor can render it
 * on same-tab navigation. These helpers keep that cache and the section picker
 * in sync with the current markdown — they are the single source of truth for
 * how markdown is turned into editor sections.
 */

export type ParsedSection = { id: string; title: string; content: string };

/**
 * Parse a markdown string into sections for the branded editor.
 * Splits on `## ` headings. Falls back to a single section if none are found.
 */
export function parseMarkdownToSections(markdown: string): ParsedSection[] {
  const sections: ParsedSection[] = [];
  let idx = 0;

  const parts = markdown.split(/^## /m);
  // parts[0] is any preamble before the first `## ` heading. After an editor
  // save, the branded markdown leads with a logo image (`![logo](url)`) which
  // is NOT a real section — skip it so it never shows in the section picker.
  const startAt = markdown.trimStart().startsWith("## ") ? 0 : 1;
  for (let p = startAt; p < parts.length; p++) {
    const part = parts[p];
    if (!part.trim()) continue;
    const newlineIdx = part.indexOf("\n");
    if (newlineIdx === -1) {
      sections.push({ id: `section-${idx++}`, title: part.trim(), content: "" });
    } else {
      sections.push({
        id: `section-${idx++}`,
        title: part.slice(0, newlineIdx).trim(),
        content: part.slice(newlineIdx + 1).trim(),
      });
    }
  }

  if (sections.length === 0 && markdown.trim()) {
    sections.push({ id: "section-0", title: "Policy Content", content: markdown.trim() });
  }

  return sections;
}

/**
 * Renumber the `N.` prefix on `## ` section headings so it reflects each
 * section's current position (1-based). Mirrors the branded editor's
 * `sectionsToPlatePages`, which strips any existing number prefix and re-applies
 * `${idx + 1}.` — that is why the editor's heading numbers always stay
 * contiguous after a section is removed. The generation page renders raw
 * markdown, so without this the AI's original numbers freeze in place and a
 * removed section leaves a gap (e.g. 1, 2, 3, 5).
 *
 * Only headings that already carry a numeric prefix (`4. ` / `4) `) are
 * rewritten — un-numbered headings are left untouched so nothing gains a number
 * it never had. Sub-headings (`###`+) and non-heading lines pass through
 * unchanged.
 */
export function renumberSectionHeadings(markdown: string): string {
  if (!markdown) return markdown;
  let counter = 0;
  return markdown
    .split("\n")
    .map((line) => {
      const match = line.match(/^##\s+(.+)$/);
      if (!match) return line;
      counter += 1;
      const rest = match[1];
      // Rewrite only if the heading already has a number prefix to renumber.
      if (!/^\d+[.)]\s*/.test(rest)) return line;
      const cleanTitle = rest.replace(/^\d+[.)]\s*/, "").trim();
      return `## ${counter}. ${cleanTitle}`;
    })
    .join("\n");
}

/**
 * Merge regenerated markdown into the existing SESSION_RESULT_KEY payload.
 *
 * Replaces `sections` + `fullContent` with the new markdown while preserving
 * everything else the generation step stored (branding, selectedTemplate, the
 * resolved cover/TOC pages, title). Returns the JSON string ready to write back
 * to sessionStorage. Safe against a null/invalid existing payload.
 */
export function mergeSessionResultContent(
  rawSession: string | null,
  markdown: string,
  fallbackTitle: string,
): string {
  let base: Record<string, unknown> = {};
  if (rawSession) {
    try {
      const parsed = JSON.parse(rawSession);
      if (parsed && typeof parsed === "object") base = parsed as Record<string, unknown>;
    } catch {
      // Invalid JSON — start from an empty payload.
    }
  }

  const title =
    typeof base.title === "string" && base.title ? base.title : fallbackTitle;

  // Drop any editor-saved pages/zoom cached in the payload: they were derived
  // from the OLD markdown, and the branded editor prefers plate-shaped pages
  // over sections/fullContent. Keeping them would make a regeneration appear
  // ignored on the same-tab session path.
  delete base.pages;
  delete base.zoom;

  return JSON.stringify({
    ...base,
    sections: parseMarkdownToSections(markdown),
    fullContent: markdown,
    title,
  });
}
